import React from 'react';
import { PlanBoardItem } from 'types';
import { MOSCOW_DOT, getTreeColor } from 'utils/canvasUtils';
import { statusPillContent } from 'utils/githubStatusColor';

export type PlanBoardCardPillsLayout = 'inline' | 'stacked';

type PlanBoardCardPillsProps = {
    item: PlanBoardItem;
    /** 'inline' renders the pills bare (no wrapper), for dropping into a caller-owned flex
     * row — the list variant's .plan-board-card__meta row (see PlanBoardCard.tsx). 'stacked'
     * splits them into a left group (priority + tree) and a right group (the GitHub issue pill,
     * if any), in an absolutely-positioned container spanning the card's width — the canvas
     * variant's selected-card popover shown above the card. */
    layout: PlanBoardCardPillsLayout;
};

/**
 * The priority + tree identity pills shown on a plan board card, plus the card's synced GitHub
 * issue pill when it has one. Shared between PlanBoardCard.tsx's list-variant meta row
 * (layout='inline') and its canvas-variant selected-card popover (layout='stacked', see
 * .plan-board-card__selected-pills in app.css) so both places render the exact same pills off
 * the same item data.
 */
export default function PlanBoardCardPills({ item, layout }: PlanBoardCardPillsProps) {
    // A standalone Enabler card has no tree identity at all (see EnablerCardItem, types.ts) — its
    // treeId/treeName are always null, so the tree pill/color below is skipped entirely for it
    // rather than being computed from a null key. A tree-sourced Enabler card (an OST enablerNode
    // surfaced here, see usePlanBoardItems.ts) carries a real treeName, same as a story/experiment
    // card, so it does get the tree pill.
    const priorityColor = item.priority ? MOSCOW_DOT[item.priority as keyof typeof MOSCOW_DOT] : undefined;
    const treeColor = item.treeName ? getTreeColor(item.treeId ?? item.treeName) : undefined;

    const identityPills = (
        <>
            {item.priority && (
                <span
                    className='plan-board-card__priority-pill'
                    style={{ '--priority-color': priorityColor ?? 'var(--muted)' } as React.CSSProperties}
                >
                    {item.priority}
                </span>
            )}
            {item.treeName && (
                <span
                    className='plan-board-card__tree-pill'
                    style={{ '--tree-color': treeColor } as React.CSSProperties}
                    title={item.treeName}
                >
                    {item.treeName}
                </span>
            )}
        </>
    );

    // The card's GitHub issue, once Sync has made one — the affordance that makes "already
    // synced" legible without opening the result modal. A plain anchor rather than a pill-shaped
    // button: it navigates away, and stopPropagation keeps the click from also selecting/dragging
    // the card underneath it.
    const issuePill = item.link && (
        <a
            className='plan-board-card__issue-pill'
            href={item.link.url}
            target='_blank'
            rel='noreferrer'
            title={`${item.link.resourceKey} #${item.link.externalId}`}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
        >
            #{item.link.externalId}
        </a>
    );

    // Where the issue currently sits in the team's workflow — its column on the GitHub project
    // board, read by Sync (see server/services/integrations/githubProvider.js's fetchItemStates).
    // A snapshot from the last Sync, not live, which is why it reads as a static pill rather than
    // anything the user can act on. Falls back to the issue's own open/closed state when it is on
    // no board.
    const status = statusPillContent(item.link);
    const statusPill = status && (
        <span
            className='plan-board-card__status-pill'
            style={{ '--status-color': status.color } as React.CSSProperties}
            title={status.title}
        >
            {status.label}
        </span>
    );

    if (layout === 'inline') {
        return (
            <>
                {identityPills}
                {statusPill}
                {issuePill}
            </>
        );
    }

    // Stacked (canvas) layout: priority + tree pills stay grouped on the left, the issue pill and
    // its board status mirror them on the right — see .plan-board-card__selected-pills in app.css.
    // The right group is a bottom-up column, so the status pill sits directly above the ticket
    // number rather than beside it.
    return (
        <div className='plan-board-card__selected-pills'>
            <div className='plan-board-card__selected-pills-left'>{identityPills}</div>
            {issuePill && (
                <div className='plan-board-card__selected-pills-right'>
                    {statusPill}
                    {issuePill}
                </div>
            )}
        </div>
    );
}
